import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import toast from "react-hot-toast";
import * as yup from "yup";
import {
  UpdateOrganizationWhitelabel,
  AddCloudflareCustomHostname,
  ListCloudflareCustomHostnames,
  GetCloudflareCustomHostnameDetails,
} from "../../../../../api";
import TextInput from "../../../../components/form/text-input";
import Switch from "../../../../components/form/switch";
import Modal from "../../../../components/modal";
import Button from "../../../../components/new-button";
import useOrganizationStore from "../../../../stores/organization";
import layoutStyles from "../../../../styles/layout.module.scss";

const schema = yup.object({
  whitelabel_domain: yup.string().trim().nullable(),
  is_whitelabel: yup.boolean(),
});

function VerificationRecordsBlock({ details }) {
  if (!details) return null;

  const txtRecords = [];

  // Certificate validation TXT records (ssl.validation_records)
  const sslRecords = details.ssl?.validation_records ?? details.validation_records ?? [];
  if (Array.isArray(sslRecords)) {
    sslRecords.forEach((r) => {
      const name = r.txt_name ?? r.name;
      const value = r.txt_value ?? r.value;
      if (name != null || value != null) {
        txtRecords.push({ name: name ?? "—", value: value ?? "—", label: "Certificate validation" });
      }
    });
  }

  // Hostname ownership verification TXT (top-level ownership_verification)
  const ov = details.ownership_verification;
  if (ov && (ov.type === "txt" || ov.name != null || ov.value != null)) {
    txtRecords.push({ name: ov.name ?? "—", value: ov.value ?? "—", label: "Hostname ownership" });
  }

  if (txtRecords.length === 0) {
    return (
      <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "var(--bg-muted, #f9fafb)", borderRadius: 6, color: "var(--text-muted, #6b7280)" }}>
        <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>DNS TXT verification records</div>
        <p style={{ margin: 0 }}>No TXT records yet — they may take a moment to appear after the hostname is created. Click &quot;Fetch TXT records&quot; to refresh.</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "0.75rem", padding: "0.75rem", background: "var(--bg-muted, #f9fafb)", borderRadius: 6 }}>
      <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>DNS TXT verification records</div>
      <p style={{ margin: "0 0 0.5rem 0", color: "var(--text-muted, #6b7280)" }}>
        Add these TXT records at your domain&apos;s authoritative DNS to verify ownership and complete SSL:
      </p>
      <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
        {txtRecords.map((r, i) => (
          <li key={i} style={{ marginBottom: "0.5rem" }}>
            {r.label && <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>{r.label}</div>}
            <div><strong>Name:</strong> <code style={{ background: "#fff", padding: "0.125rem 0.25rem", borderRadius: 4 }}>{r.name}</code></div>
            <div><strong>Value:</strong> <code style={{ background: "#fff", padding: "0.125rem 0.25rem", borderRadius: 4, wordBreak: "break-all" }}>{r.value}</code></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const WhitelabelSettingsModal = ({ open, onClose }) => {
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.id,
  );
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData,
  );

  const [cloudflareAdding, setCloudflareAdding] = useState(false);
  const [cloudflareList, setCloudflareList] = useState([]);
  const [cloudflareListLoading, setCloudflareListLoading] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [verificationDetailsLoading, setVerificationDetailsLoading] = useState(false);

  const organization = selectedOrganizationData?.organization;
  const initialValues = {
    whitelabel_domain: organization?.whitelabel_domain ?? "",
    is_whitelabel: organization?.is_whitelabel ?? false,
  };

  const fetchCloudflareHostnames = async () => {
    if (!selectedOrganizationId) return;
    setCloudflareListLoading(true);
    try {
      const res = await ListCloudflareCustomHostnames(selectedOrganizationId);
      if (res?.success && Array.isArray(res.custom_hostnames)) {
        setCloudflareList(res.custom_hostnames);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCloudflareListLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedOrganizationId) {
      fetchCloudflareHostnames();
    }
  }, [open, selectedOrganizationId]);

  useEffect(() => {
    const whitelabelDomain = (organization?.whitelabel_domain ?? "").trim().toLowerCase();
    const related = whitelabelDomain
      ? cloudflareList.filter((ch) => (ch.hostname || "").toLowerCase() === whitelabelDomain)
      : [];
    if (related.length === 0) {
      setVerificationDetails(null);
    }
  }, [cloudflareList, organization?.whitelabel_domain]);

  useEffect(() => {
    if (!open || !organization?.is_whitelabel || cloudflareListLoading) return;
    const whitelabelDomain = (organization?.whitelabel_domain ?? "").trim().toLowerCase();
    if (!whitelabelDomain) return;
    const related = cloudflareList.filter((ch) => (ch.hostname || "").toLowerCase() === whitelabelDomain);
    const firstId = related[0]?.id;
    if (firstId && verificationDetails?.id !== firstId) {
      fetchVerificationDetails(firstId, { silent: true });
    }
  }, [open, organization?.is_whitelabel, organization?.whitelabel_domain, cloudflareList, cloudflareListLoading, verificationDetails?.id]);

  const fetchVerificationDetails = async (customHostnameId, options = {}) => {
    const { silent = false } = options;
    if (!selectedOrganizationId || !customHostnameId) return;
    setVerificationDetailsLoading(true);
    setVerificationDetails(null);
    try {
      const res = await GetCloudflareCustomHostnameDetails(selectedOrganizationId, customHostnameId);
      if (res?.success && res?.custom_hostname) {
        setVerificationDetails(res.custom_hostname);
        if (!silent) {
          toast.success("TXT records loaded.");
        }
      }
    } catch (err) {
      console.error(err);
      if (!silent) {
        toast.error("Failed to load verification records.");
      }
    } finally {
      setVerificationDetailsLoading(false);
    }
  };

  const checkValidation = async (customHostnameId) => {
    if (!selectedOrganizationId || !customHostnameId) return;
    setVerificationDetailsLoading(true);
    try {
      await fetchCloudflareHostnames();
      const res = await GetCloudflareCustomHostnameDetails(selectedOrganizationId, customHostnameId);
      if (res?.success && res?.custom_hostname) {
        const ch = res.custom_hostname;
        setVerificationDetails(ch);
        const hostnameStatus = ch.status ?? "—";
        const sslStatus = ch.ssl?.status ?? "—";
        const isFullyValidated = hostnameStatus === "active" && sslStatus === "active";
        if (isFullyValidated) {
          toast.success("Validation complete. Hostname and SSL are active.");
        } else {
          toast.success(`Status updated — Hostname: ${hostnameStatus}, SSL: ${sslStatus}`);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to check validation status.");
    } finally {
      setVerificationDetailsLoading(false);
    }
  };

  const handleAddCloudflareHostname = async (hostname) => {
    const trimmed = hostname?.trim();
    if (!trimmed) {
      toast.error("Enter a whitelabel domain above first.");
      return;
    }
    setCloudflareAdding(true);
    const toastId = toast.loading("Adding custom hostname to Cloudflare...");
    try {
      const res = await AddCloudflareCustomHostname(selectedOrganizationId, {
        hostname: trimmed,
      });
      if (res?.success) {
        toast.success(
          res?.message ?? "Custom hostname added. Complete validation in Cloudflare.",
          { id: toastId, duration: 5000 },
        );
        fetchCloudflareHostnames();
      } else {
        toast.error(res?.message ?? "Failed to add custom hostname.", {
          id: toastId,
        });
      }
    } catch (err) {
      toast.error("Something went wrong, please try again.", { id: toastId });
      console.error(err);
    } finally {
      setCloudflareAdding(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const toastId = toast.loading("Saving whitelabel settings...");
    try {
      const res = await UpdateOrganizationWhitelabel(selectedOrganizationId, {
        whitelabel_domain: values.whitelabel_domain || null,
        is_whitelabel: values.is_whitelabel,
      });
      if (res?.success) {
        setSelectedOrganizationData((prev) => ({
          ...prev,
          organization: {
            ...prev?.organization,
            whitelabel_domain: values.whitelabel_domain || null,
            is_whitelabel: values.is_whitelabel,
          },
        }));
        onClose();
        toast.success("Whitelabel settings saved successfully.", {
          id: toastId,
          duration: 5000,
        });
      } else {
        toast.error(res?.message ?? "Failed to save whitelabel settings.", {
          id: toastId,
        });
      }
    } catch (err) {
      toast.error("Something went wrong, please try again.", { id: toastId });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      header="Whitelabel settings"
      size="sm"
    >
      <Formik
        enableReinitialize
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validateOnBlur={false}
      >
        {({
          handleSubmit,
          handleChange,
          setFieldValue,
          values,
          touched,
          errors,
          isSubmitting,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className={`${layoutStyles.grid} ${layoutStyles.gap2}`}>
              <TextInput
                label="Whitelabel domain"
                id="whitelabel_domain"
                name="whitelabel_domain"
                value={values.whitelabel_domain ?? ""}
                onChange={handleChange}
                touched={touched.whitelabel_domain}
                errored={errors.whitelabel_domain}
                error={errors.whitelabel_domain}
                placeholder="e.g. agent.example.com"
              />
              <Switch
                id="is_whitelabel"
                name="is_whitelabel"
                label="Is whitelabel"
                checked={!!values.is_whitelabel}
                onChange={(e) =>
                  setFieldValue("is_whitelabel", e.target.checked)
                }
              />

              {values.is_whitelabel && (
                <div
                  className={`${layoutStyles.flex} ${layoutStyles.flexCol} ${layoutStyles.gap1}`}
                  style={{ marginTop: "0.5rem", paddingTop: "1rem", borderTop: "1px solid var(--border-color, #e5e7eb)" }}
                >
                  <span style={{ fontWeight: 600 }}>
                    Cloudflare custom hostname
                  </span>
                  <p style={{ margin: 0, color: "var(--text-muted, #6b7280)" }}>
                    Add the whitelabel domain above to your Cloudflare zone (SSL for SaaS). Complete DNS and certificate validation in the Cloudflare dashboard.
                  </p>
                  {(() => {
                    const whitelabelDomain = (values.whitelabel_domain || organization?.whitelabel_domain || "").trim().toLowerCase();
                    const relatedRecords = whitelabelDomain
                      ? cloudflareList.filter((ch) => (ch.hostname || "").toLowerCase() === whitelabelDomain)
                      : [];
                    const alreadyAdded = relatedRecords.length > 0;
                    const firstRelatedId = relatedRecords[0]?.id;
                    return (
                      <>
                        <div className={`${layoutStyles.flex} ${layoutStyles.gap1}`} style={{ alignItems: "center" }}>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleAddCloudflareHostname(values.whitelabel_domain)}
                            disabled={cloudflareAdding || !(values.whitelabel_domain || "").trim() || alreadyAdded}
                          >
                            {cloudflareAdding ? "Adding…" : alreadyAdded ? "Already in Cloudflare" : "Add to Cloudflare"}
                          </Button>
                        </div>
                        {cloudflareListLoading ? (
                          <div style={{ color: "var(--text-muted, #6b7280)" }}>
                            Loading custom hostnames…
                          </div>
                        ) : alreadyAdded ? (
                          <div>
                            <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
                              Custom hostname for this domain:
                            </div>
                            {relatedRecords.map((ch) => {
                              const statusCh = verificationDetails?.id === ch.id ? verificationDetails : ch;
                              return (
                                <div key={ch.id} style={{ marginBottom: "0.5rem" }}>
                                  <code style={{ background: "var(--bg-muted, #f3f4f6)", padding: "0.125rem 0.375rem", borderRadius: 4 }}>
                                    {ch.hostname}
                                  </code>
                                  {" — Hostname: "}
                                  {statusCh.status ?? "—"}
                                  {", SSL: "}
                                  {statusCh.ssl?.status ?? "—"}
                                </div>
                              );
                            })}
                            <div className={`${layoutStyles.flex} ${layoutStyles.gap1}`} style={{ flexWrap: "wrap", marginTop: "0.5rem" }}>
                              <Button
                                type="button"
                                variant="secondary"
                                size="small"
                                onClick={() => fetchVerificationDetails(firstRelatedId)}
                                disabled={verificationDetailsLoading}
                              >
                                {verificationDetailsLoading ? "Loading…" : "Fetch TXT records"}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="small"
                                onClick={() => checkValidation(firstRelatedId)}
                                disabled={verificationDetailsLoading}
                              >
                                Check validation
                              </Button>
                            </div>
                            {verificationDetailsLoading && verificationDetails?.id !== firstRelatedId ? (
                              <div style={{ marginTop: "0.5rem", color: "var(--text-muted, #6b7280)" }}>
                                Loading…
                              </div>
                            ) : (() => {
                              const detailsToShow = verificationDetails?.id === firstRelatedId
                                ? verificationDetails
                                : relatedRecords[0] ?? null;
                              return detailsToShow ? (
                                <VerificationRecordsBlock details={detailsToShow} />
                              ) : null;
                            })()}
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              )}

              <div
                className={`${layoutStyles.flex} ${layoutStyles.flexEnd} ${layoutStyles.gap1}`}
              >
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default WhitelabelSettingsModal;
